import { Injectable } from '@angular/core';
import { PersonaService } from '@core/services/persona.service';
import {
  DsmSelectors,
  PersonaSelectors,
  ProductsSelectors,
  UnderlyingPolicySelector,
  VersionedQuoteSelectors,
} from '@core/store/selectors';
import {
  getProtectorVersionEntities,
  getCoreVersionEntities,
  awaitingVersionChoice,
} from './versioned-quote.selector';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  combineLatest,
  concatMap,
  EMPTY,
  delayWhen,
  filter,
  first,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
  distinctUntilChanged,
  timeout,
} from 'rxjs';
import { VersionedQuoteService } from './versioned-quote.service';
import { Store } from '@ngrx/store';
import {
  getStatusOfAllProducts,
  getSelectedProductsWithoutErrors,
} from '@entities/product/product.selectors';
import {
  filterOutNull,
  filterOutUndefined,
} from '@shared/rxjs/filter-out-null.operator';
import {
  VersionedQuoteActions,
  QuoteActions,
  WatercraftExcludedExposureActions,
  WatercraftExposureActions,
  LocationExposureActions,
  LocationExcludedExposureActions,
  VehicleExcludedExposureActions,
  VehicleExposureActions,
  UnderlyingPolicyActions,
  ProductActions,
} from '@core/store/actions';
import { QuoteAdapter } from '@core/adapters/quote.adapter';
import { CoveragesService } from '@core/services/coverages.service';
import { RatingTypes } from '@shared/constants/app-constants';
import { RatingType } from '@core/models/api/dsm-types';
import { setHubLoaded } from '@entities/session/session.action';
import { concatLatestFrom } from '@ngrx/operators';
import { EligibleDiscountsService } from '@core/services/eligible-discounts.service';
import {
  AllExposures,
  getAllExposures,
} from '@entities/umbrella/umbrella.selector';
import { UnderlyingPolicyService } from '@core/services/underlying-policy.service';
import { VersionedQuoteEntity } from './versioned-quote.reducer';
import { UnderlyingPolicyEntity } from '@entities/underlying-policy/underlying-policy.entity';
import { VehicleExposureService } from '@core/services/vehicle-exposure.service';
import { WatercraftExposureService } from '@core/services/watercraft-exposure.service';
import { LocationExposureService } from '@core/services/location-exposure.service';
import { DsmService } from '@entities/dsm/dsm.service';
import { LogService } from '@core/services/log.service';

@Injectable({
  providedIn: 'root',
})
export class VersionedQuoteEffects {
  constructor(
    private personaService: PersonaService,
    private versionedQuoteService: VersionedQuoteService,
    private actions$: Actions,
    private store: Store,
    private quoteAdapter: QuoteAdapter,
    private coveragesService: CoveragesService,
    private eligibleDiscountsService: EligibleDiscountsService,
    private underlyingPolicyService: UnderlyingPolicyService,
    private vehicleExposureService: VehicleExposureService,
    private locationExposureService: LocationExposureService,
    private watercraftExposureService: WatercraftExposureService,
    private dsmService: DsmService,
    private logService: LogService
  ) {}

  initiateVersioningOnPersonaChoice$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setHubLoaded),
        switchMap(() => this.waitForValidVersioningState()),
        switchMap(() => this.executeVersioningSequence())
      ),
    { dispatch: false }
  );

  updateQuoteVersionWithRecommendations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VersionedQuoteActions.addPerResponse),
      withLatestFrom(this.personaService.getPersonaReport()),
      concatMap(([action, personaReport]) =>
        this.coveragesService.getQuoteCoverages(action.productType).pipe(
          take(1),
          map((coverages) => ({
            action,
            personaReport,
            coverages: coverages || [],
          }))
        )
      ),
      concatMap(({ action, personaReport, coverages }) => {
        const protectorQuoteVersion = action.response[0]; // currently this action will only have protector version in response
        if (!protectorQuoteVersion) {
          return of();
        }

        const updateQuoteRequest =
          this.versionedQuoteService.buildUpdateQuoteRequestFromPersona(
            personaReport,
            action.productType,
            protectorQuoteVersion.quoteId
          );
        const updateCoverageRequest =
          this.versionedQuoteService.buildCoverageChangeRequestFromPersona(
            personaReport,
            action.productType,
            protectorQuoteVersion.quoteId,
            coverages
          );
        const updateDiscountRequests =
          this.versionedQuoteService.buildEligibleDiscountRequestsFromPersona(
            personaReport,
            action.productType,
            protectorQuoteVersion.quoteId
          );

        const calls: Observable<any>[] = [];
        if (updateQuoteRequest) {
          calls.push(this.quoteAdapter.updateQuote(updateQuoteRequest));
        }
        if (updateCoverageRequest) {
          calls.push(this.quoteAdapter.updateCoverages(updateCoverageRequest));
        }
        if (updateDiscountRequests?.length > 0) {
          updateDiscountRequests.forEach((discountRequest) => {
            calls.push(
              this.eligibleDiscountsService.updatePolicyLine(discountRequest)
            );
          });
        }

        if (calls.length < 1) {
          return of();
        }

        return forkJoin(calls).pipe(
          map((responses) => {
            return VersionedQuoteActions.updateQuoteVersionWithRecommendationsSuccess(
              {
                productType: action.productType,
                quoteId: protectorQuoteVersion.quoteId,
              }
            );
          }),
          catchError((error) => {
            this.versionedQuoteService.setAwaitingVersionChoice(false);
            return of(
              VersionedQuoteActions.updateQuoteVersionWithRecommendationsFail({
                productType: action.productType,
                quoteId: protectorQuoteVersion.quoteId,
                error,
              })
            );
          })
        );
      })
    )
  );

  rateVersionedQuoteAfterApplyingPersona$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VersionedQuoteActions.updateQuoteVersionWithRecommendationsSuccess
      ),
      mergeMap((action) =>
        this.quoteAdapter
          .rateQuote({
            productType: action.productType,
            quoteId: action.quoteId,
            ratingType: RatingTypes.QUOTE as RatingType,
            suppressCallsInFlight: true,
            suppressQuoteStatusUpdate: true,
          })
          .pipe(
            map((response) => {
              return VersionedQuoteActions.rateVersionedQuoteSuccess({
                response: response,
                quoteId: action.quoteId,
                productType: action.productType,
              });
            }),
            catchError((error) => {
              this.versionedQuoteService.setAwaitingVersionChoice(false);
              return of(
                VersionedQuoteActions.rateVersionedQuoteFail({
                  error: error,
                  productType: action.productType,
                  quoteId: action.quoteId,
                })
              );
            })
          )
      )
    )
  );

  setProtectorVersionLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        VersionedQuoteActions.rateVersionedQuoteSuccess,
        VersionedQuoteActions.rateVersionedQuoteFail
      ),
      withLatestFrom(
        this.store.select(
          VersionedQuoteSelectors.getPendingVersionedRateCallsCount
        )
      ),
      filter(([action, pendingCallCount]) => pendingCallCount === 0),
      map(() => VersionedQuoteActions.setProtectorVersionLoaded())
    )
  );

  logOnProtectorVersionLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(VersionedQuoteActions.setProtectorVersionLoaded),
        distinctUntilChanged(),
        withLatestFrom(
          this.store.select(PersonaSelectors.getPersonaName),
          this.store.select(awaitingVersionChoice)
        ),
        filter(([,,awaiting]) => awaiting === true),
        tap(([, persona]) =>
          this.logService.logBusinessEvent('persona-recommended-on-tabbed-hub', persona)
        )
      ),
    { dispatch: false }
  );

  logOnNonTabbedHubLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          VersionedQuoteActions.setAwaitingVersionChoice,
          QuoteActions.rateQuoteFail,
          VersionedQuoteActions.continueWithCoreQuoteSuccess,
          VersionedQuoteActions.continueWithProtectorQuoteSuccess,
        ),
        withLatestFrom(this.store.select(awaitingVersionChoice)),
        distinctUntilChanged((previous, current) => previous[1] === current[1]),
        filter(([, awaiting]) => awaiting === false),
        concatLatestFrom(() => [
          this.store.select(PersonaSelectors.getPersonaName),
        ]),
        tap(([_, persona]) => {
          if (persona) {
            this.logService.logBusinessEvent('persona-recommended-on-hub', persona);
          }
        })
      ),
    { dispatch: false }
  );

  continueWithCoreQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VersionedQuoteActions.continueWithCoreQuote),
      delayWhen(() => {
        return this.store
          .select(DsmSelectors.callInFlightBySubstring('delete-quote-version'))
          .pipe(
            filter((deleting) => !deleting),
            take(1),
            switchMap(() =>
               combineLatest([
                this.store.select(getCoreVersionEntities),
                this.store.select(VersionedQuoteSelectors.selectBaseQuotes),
              ]).pipe(
                filter(
                  ([coreVersions, baseQuotes]) =>
                    Array.isArray(coreVersions) &&
                    coreVersions.length > 0 &&
                    Array.isArray(baseQuotes) &&
                    coreVersions.length === baseQuotes.length
                ),
                take(1),
                timeout(3000),
                catchError(() => of(null))
              )
            )
          );
      }),
      withLatestFrom(
        this.store.select(getCoreVersionEntities),
        this.store.select(ProductsSelectors.getSelectedProductEntities),
        this.store.select(UnderlyingPolicySelector.getAllUnderlyingPolicies),
        this.store.select(getAllExposures),
        this.store.select(
          ProductsSelectors.getQuoteIdForSelectedProduct('PersonalUmbrella')
        ),
      ),
      switchMap(([
        action,
        coreVersions,
        selectedProducts,
        underlyingPolicies,
        exposures,
        umbrellaQuoteId
      ]) => {
        if (coreVersions.length === 0) {
          return of(VersionedQuoteActions.continueWithCoreQuoteSuccess({
            quoteIdMappings: [],
          }));
        }

        const updateCalls = coreVersions.map((entity) =>
          this.quoteAdapter.updateQuoteVersion(
            entity.productType,
            entity.quoteId,
            entity.versionNumber,
            true,
            'Core Quote'
          )
        );

        if (umbrellaQuoteId) {
          this.generateUpdateCallsForUmbrellaBits(
            updateCalls,
            coreVersions, 
            underlyingPolicies,
            exposures,
            umbrellaQuoteId
          );
        }

        return forkJoin(updateCalls).pipe(
          map(() => {
            const quoteIdMappings = coreVersions.map(
              (coreEntity) => {
                const selectedEntity = selectedProducts.find(
                  (selected) => selected.type === coreEntity.productType &&
                    selected.quoteId?.split('-')[0] === coreEntity.quoteId?.split('-')[0] &&
                    selected.quoteId != coreEntity.quoteId
                );
                return {
                  productType: coreEntity.productType,
                  selectedQuoteId: coreEntity.quoteId,
                  baseQuoteId: selectedEntity?.quoteId || '',
                };
              }
            );
            return VersionedQuoteActions.continueWithCoreQuoteSuccess({
              quoteIdMappings
            });
          }),
          catchError(() => {
            return of(VersionedQuoteActions.continueWithCoreQuoteSuccess({
              quoteIdMappings: [],
            }));
          })
        );
      })
    )
  );

  continueWithProtectorQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VersionedQuoteActions.continueWithProtectorQuote),
      withLatestFrom(
        this.store.select(getProtectorVersionEntities),
        this.store.select(getCoreVersionEntities),
        this.store.select(UnderlyingPolicySelector.getAllUnderlyingPolicies),
        this.store.select(getAllExposures),
        this.store.select(
          ProductsSelectors.getQuoteIdForSelectedProduct('PersonalUmbrella')
        ),
      ),
      switchMap(
        ([
          action,
          protectorVersions,
          coreVersions,
          underlyingPolicies,
          exposures,
          umbrellaQuoteId,
        ]) => {
          if (protectorVersions.length === 0) {
            return of(
              VersionedQuoteActions.continueWithProtectorQuoteSuccess({
                quoteIdMappings: [],
              })
            );
          }

          const updateCalls: Observable<unknown>[] = protectorVersions.map(
            (entity) =>
              this.quoteAdapter.updateQuoteVersion(
                entity.productType,
                entity.quoteId,
                entity.versionNumber,
                true,
                'Protector Quote'
              )
          );

          if (umbrellaQuoteId) {
            this.generateUpdateCallsForUmbrellaBits(
              updateCalls,
              protectorVersions,
              underlyingPolicies,
              exposures,
              umbrellaQuoteId
            );
          }

          return forkJoin(updateCalls).pipe(
            map(() => {
              const quoteIdMappings = protectorVersions.map(
                (protectorEntity) => {
                  const coreVersionEntity = coreVersions.find(
                    (core) => core.productType === protectorEntity.productType
                  );
                  return {
                    productType: protectorEntity.productType,
                    selectedQuoteId: protectorEntity.quoteId,
                    baseQuoteId: coreVersionEntity?.quoteId || '',
                  };
                }
              );

              return VersionedQuoteActions.continueWithProtectorQuoteSuccess({
                quoteIdMappings,
              });
            }),
            catchError((error) => {
              return of(
                VersionedQuoteActions.continueWithProtectorQuoteSuccess({
                  quoteIdMappings: [],
                })
              );
            })
          );
        }
      )
    )
  );

  private generateUpdateCallsForUmbrellaBits(
    updateCalls: Observable<unknown>[],
    protectorVersions: VersionedQuoteEntity[],
    underlyingPolicies: UnderlyingPolicyEntity[],
    exposures: AllExposures,
    umbrellaQuoteId: string
  ): void {
    for (const underlyingPolicy of underlyingPolicies) {
      const quoteNumber = (underlyingPolicy.underlyingPolicyNumber || '').split(
        '-'
      )[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== underlyingPolicy.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.underlyingPolicyService
            .addOrUpdateUnderlyingPolicy({
              quoteId: umbrellaQuoteId,
              underlyingPolicyBody: {
                underlyingPolicy: {
                  ...underlyingPolicy,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  UnderlyingPolicyActions.upsertUnderlyingPolicySuccess({
                    payload: rsp,
                    existingPolicyNumber: '',
                  })
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.vehicleExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.vehicleExposureService
            .addOrUpdateVehicleExposure({
              quoteId: umbrellaQuoteId,
              vehicleExposureBody: {
                vehicleExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  VehicleExposureActions.upsertVehicleExposureSuccess({
                    payload: rsp,
                  })
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.vehicleExcludedExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.vehicleExposureService
            .addOrUpdateExcludedVehicleExposure({
              quoteId: umbrellaQuoteId,
              vehicleExcludedExposureBody: {
                vehicleExcludedExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  VehicleExcludedExposureActions.upsertVehicleExcludedExposureSuccess(
                    {
                      payload: rsp,
                    }
                  )
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.locationExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.locationExposureService
            .addOrUpdateLocationExposure({
              quoteId: umbrellaQuoteId,
              locationExposureBody: {
                locationExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  LocationExposureActions.upsertLocationExposureSuccess({
                    payload: rsp,
                    oldLocationExposureId: '',
                  })
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.locationExcludedExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.locationExposureService
            .addOrUpdateLocationExcludedExposure({
              quoteId: umbrellaQuoteId,
              locationExcludedExposureBody: {
                locationExcludedExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  LocationExcludedExposureActions.upsertLocationExcludedExposureSuccess(
                    {
                      payload: rsp,
                    }
                  )
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.watercraftExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.watercraftExposureService
            .addOrUpdateWatercraftExposure({
              quoteId: umbrellaQuoteId,
              watercraftExposureBody: {
                watercraftExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  WatercraftExposureActions.upsertWatercraftExposureSuccess({
                    payload: rsp,
                  })
                )
              )
            )
        );
      }
    }
    for (const exposure of exposures.watercraftExcludedExposures) {
      const quoteNumber = (exposure.underlyingPolicyNumber || '').split('-')[0];
      const protectorVersion = protectorVersions.find(
        (v) => v.quoteNumber === quoteNumber
      );
      if (
        protectorVersion &&
        protectorVersion.quoteId !== exposure.underlyingPolicyNumber
      ) {
        updateCalls.push(
          this.watercraftExposureService
            .addOrUpdateWatercraftExcludedExposure({
              quoteId: umbrellaQuoteId,
              watercraftExcludedExposureBody: {
                watercraftExcludedExposure: {
                  ...exposure,
                  underlyingPolicyNumber: protectorVersion.quoteId,
                },
              },
            })
            .pipe(
              tap((rsp) =>
                this.store.dispatch(
                  WatercraftExcludedExposureActions.upsertWatercraftExcludedExposureSuccess(
                    {
                      payload: rsp,
                    }
                  )
                )
              )
            )
        );
      }
    }
  }

  deleteUnselectedVersionsAfterSelection$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          VersionedQuoteActions.continueWithProtectorQuoteSuccess,
          VersionedQuoteActions.continueWithCoreQuoteSuccess
        ),
        // wait until rating is finished to delete protectors versions
       delayWhen((action) => {
          if (action.type !== VersionedQuoteActions.continueWithCoreQuoteSuccess.type) {
            return of(null);
          }
          return this.store
            .select(getProtectorVersionEntities)
            .pipe(
              filter(
                (entities) =>
                  Array.isArray(entities) &&
                  entities.length > 0 &&
                  entities.every((e) => e && e.ratingResponse !== undefined)
              ),
              take(1)
            );
        }),
        concatLatestFrom(() =>
          this.store
            .select(VersionedQuoteSelectors.selectVersionedQuotes)
            .pipe(filterOutUndefined(), take(1))
        ),
        switchMap(([action, allEntities]) => {
          if (allEntities.length === 0) {
            return of();
          }

          const isProtectorSelection =
            action.type ===
            VersionedQuoteActions.continueWithProtectorQuoteSuccess.type;

          const uniqueProductTypes = allEntities
            .map((entity) => entity.productType)
            .filter(
              (productType, index, arr) => arr.indexOf(productType) === index
            );

          const byProduct = uniqueProductTypes.map((productType) => {
            return allEntities
              .filter((entity) => entity.productType === productType)
              .map((entity) => ({
                quoteId: entity.quoteId,
                quoteNumber: entity.quoteNumber,
                versionNumber: +entity.versionNumber,
                versionDescription: entity.versionDescription,
                isSelected: isProtectorSelection
                  ? entity.isProtectorVersion === true
                  : entity.isProtectorVersion === false,
              }));
          });

          return this.versionedQuoteService
            .deleteExcessVersions(byProduct)
            .pipe(
              map(() => null),
              catchError(() => {
                return of(null);
              })
            );
        }),
        filterOutNull()
      ),
    { dispatch: false }
  );

  rerateAfterProtectorSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VersionedQuoteActions.continueWithProtectorQuoteSuccess),
      map(() => QuoteActions.rateQuoteSelectedProducts({ ratingType: 'quote' }))
    )
  );

  syncBaseQuotesWithSelectedProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.updateSelectedProducts),
      concatLatestFrom(() =>
        this.store.select(getSelectedProductsWithoutErrors)
      ),
      map(([_, products]) =>
        VersionedQuoteActions.updateBaseQuotes({
          baseQuotes: products
            .filter((p) => p.quoteId)
            .map((p) => ({
              productType: p.type,
              quoteNumber: p.quoteId?.split('-')[0] || '',
              isVersioningEnabled: false,
            })),
        })
      )
    )
  );

  private waitForValidVersioningState(): Observable<unknown> {
    let hadErrorStatus = false;

    return combineLatest([
      this.store.select(getStatusOfAllProducts),
      this.store.select(VersionedQuoteSelectors.isProtectorVersionLoaded),
    ]).pipe(
      tap(([quoteStatuses]) => quoteStatuses.some((s) => s === 'Error') ? hadErrorStatus = true : null),
      switchMap((values) => this.dsmService.waitForDsmCallsIdle().then(() => values)),
      first(
        ([quoteStatuses, protectorVersionLoaded]) =>
          quoteStatuses.length > 0 &&
          quoteStatuses.every((s) => s === 'Quoted' || s === 'Binding') &&
          !protectorVersionLoaded
      ),
      switchMap(() => {
        if (hadErrorStatus) {
          return EMPTY;
        }
        return this.personaService.orchestratePersonaConsideration().pipe(
          withLatestFrom(this.versionedQuoteService.allVersionRecommendationsPresent()),
          take(1),
          filter(([persona, allRecommendationsPresent]) => {
            const pass =
              persona && 
              persona !== 'None' && 
              persona !== 'Other' && 
              !allRecommendationsPresent;

            this.versionedQuoteService.setAwaitingVersionChoice(pass);
            return pass;
          })
        );
      })
    );
  }

  private executeVersioningSequence(): Observable<unknown> {
    return this.store.select(VersionedQuoteSelectors.selectBaseQuotes).pipe(
      take(1),
      switchMap((baseQuotes) =>
        this.versionedQuoteService.makeAllQuotesVersionable(baseQuotes)
      ),
      switchMap(() => this.versionedQuoteService.refreshVersions()),
      switchMap((versions) =>
        this.versionedQuoteService.deleteExcessVersions(versions)
      ),
      switchMap(() =>
        this.versionedQuoteService.generateProtectorsVersions()
      ),
      catchError((e) => {
        this.versionedQuoteService.setAwaitingVersionChoice(false);
        return [];
      })
    );
  }
}
